import { useEffect, useState } from 'react';
import API from './api';

// raise this number when the server's location lists change, so a browser never keeps showing an older answer
const LIST_VERSION = 3;
const cache = new Map();
const request = (path, params, key) => {
    const cacheKey = `${path}:${JSON.stringify(params)}`;
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, API.get(`/locations/${path}`, { params: { ...params, v: LIST_VERSION } }).then(res => res.data[key] || []).catch(() => []));
    }
    return cache.get(cacheKey);
};

/**
 * Where an Indian PIN code is: { country, state, district, districtOther, city } ready to put into the form fields,
 * or null when the PIN is not found / the service cannot be reached. A district that is not in our list for the state
 * comes back as district 'Other' with the real name in districtOther.
 */
export const lookupPin = async (pin) => {
    if (!/^\d{6}$/.test(String(pin))) return null;
    try {
        const { data } = await API.get('/locations/pincode', { params: { pin, v: LIST_VERSION } });
        if (!data.found) return null;
        const districts = await request('districts', { country: data.country, state: data.state }, 'districts');
        const listed = districts.includes(data.district);
        return {
            country: data.country, state: data.state, city: data.city || '',
            district: listed ? data.district : 'Other', districtOther: listed ? '' : data.district,
        };
    } catch {
        return null;
    }
};

export const useLocationLists = (country, state, district) => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [cities, setCities] = useState([]);

    useEffect(() => { request('countries', {}, 'countries').then(setCountries); }, []);
    useEffect(() => {
        setStates([]); setDistricts([]); setCities([]);
        if (!country) return;
        request('states', { country }, 'states').then(setStates);
    }, [country]);
    useEffect(() => {
        setDistricts([]); setCities([]);
        if (!country || !state) return;
        request('districts', { country, state }, 'districts').then(setDistricts);
    }, [country, state]);
    useEffect(() => {
        setCities([]);
        if (!country || !state) return;
        request('cities', { country, state, ...(district ? { district } : {}) }, 'cities').then(setCities);
    }, [country, state, district]);

    return { countries, states, districts, cities };
};
